// Types
import type {
  TAutomationModeOption,
  TSemiAutomaticAutoSendOption,
} from '~/modules/opportunities/automated-email/settings/types';
import { EAutomationMode } from '~/modules/opportunities/automated-email/types';

// Assets
import modeOffIcon from '~/assets/images/icons/lock.svg';
import modeSemiIcon from '~/assets/images/icons/list.svg';
import modeFullyIcon from '~/assets/images/icons/star.svg';

/** Flip to `false` when fully-automatic is ready for production. */
const FULLY_AUTOMATIC_MODE_DISABLED: boolean = false;

/** Flip to `false` to allow timed auto-send again (manual-only for now). */
const SEMI_AUTO_SEND_TIMED_OPTIONS_DISABLED: boolean = false;

/** Flip to `false` to stop seeding withdrawn triggers. Applies to sales only. */
const WITHDRAWN_TEMPLATES_ENABLED: boolean = true;

const AUTOMATION_MODE_ICON: Record<string, string> = {
  'automation-mode-off': modeOffIcon,
  'automation-mode-semi': modeSemiIcon,
  'automation-mode-fully': modeFullyIcon,
};

const AUTOMATION_MODE_ICON_CLASS: Record<string, string> = {
  'automation-mode-off': 'automated-email-settings-ui__option-icon--surface',
  'automation-mode-semi': 'automated-email-settings-ui__option-icon--semi-automatic',
  'automation-mode-fully': 'automated-email-settings-ui__option-icon--fully-automatic',
};

const AUTOMATION_MODE_BADGE_CLASS: Record<string, string> = {
  'automation-mode-off': 'automated-email-settings-ui__option-badge--muted',
  'automation-mode-semi': 'automated-email-settings-ui__option-badge--info',
  'automation-mode-fully': 'automated-email-settings-ui__option-badge--success',
};

const AUTOMATION_MODE_BADGE_LABEL: Record<string, string> = {
  'automation-mode-off': 'Disabled',
  'automation-mode-semi': 'Review required',
  'automation-mode-fully': 'No review needed',
};

const AUTOMATION_MODE_OPTIONS: TAutomationModeOption[] = [
  {
    id: 'automation-mode-off',
    value: EAutomationMode.OFF,
    title: 'Off',
    description: 'Automatic email functionality is disabled. No emails will be generated or sent.',
  },
  {
    id: 'automation-mode-semi',
    value: EAutomationMode.SEMI_AUTOMATIC,
    title: 'Semi-Automated',
    description:
      "Emails are generated and placed in the Outbox. You can review, send, or dismiss each email individually before it's delivered.",
  },
  {
    id: 'automation-mode-fully',
    value: EAutomationMode.FULLY_AUTOMATIC,
    title: 'Fully-Automated',
    description:
      'Emails are generated and sent automatically based on triggers and templates. No manual review is required.',
    disabled: FULLY_AUTOMATIC_MODE_DISABLED,
  },
];

const CONTACT_FREQUENCY_MIN: number = 1;
const CONTACT_FREQUENCY_MAX: number = 90;

/** Default when API omits `semi_automatic_auto_send_days` — “Send in a day”. */
const DEFAULT_SEMI_AUTOMATIC_AUTO_SEND_DAYS: number = 1;

const SEMI_AUTOMATIC_AUTO_SEND_OPTIONS: TSemiAutomaticAutoSendOption[] = [
  { label: 'Send manually', days: 0 },
  {
    label: 'Send in 24 hours',
    days: 1,
    disabled: SEMI_AUTO_SEND_TIMED_OPTIONS_DISABLED,
  },
  {
    label: 'Send in 48 hours',
    days: 2,
    disabled: SEMI_AUTO_SEND_TIMED_OPTIONS_DISABLED,
  },
  {
    label: 'Send in 72 hours',
    days: 3,
    disabled: SEMI_AUTO_SEND_TIMED_OPTIONS_DISABLED,
  },
];

const SEMI_AUTOMATIC_AUTO_SEND_DAY_VALUES: ReadonlySet<number> = new Set<number>(
  SEMI_AUTOMATIC_AUTO_SEND_OPTIONS.map((o: TSemiAutomaticAutoSendOption): number => o.days)
);

const DEFAULT_UNCONFIGURED_CONTACT_FREQUENCY: number = 30;

/** Lettings: Off + Semi-automated (manual) only; Fully + timed semi stay disabled. */
const getAutomationModeOptions = (isLettings: boolean): TAutomationModeOption[] =>
  AUTOMATION_MODE_OPTIONS.map(
    (option: TAutomationModeOption): TAutomationModeOption =>
      option.value === EAutomationMode.FULLY_AUTOMATIC
        ? { ...option, disabled: FULLY_AUTOMATIC_MODE_DISABLED || isLettings }
        : option
  );

const getSemiAutomaticAutoSendOptions = (isLettings: boolean): TSemiAutomaticAutoSendOption[] =>
  SEMI_AUTOMATIC_AUTO_SEND_OPTIONS.map(
    (option: TSemiAutomaticAutoSendOption): TSemiAutomaticAutoSendOption =>
      option.days === 0
        ? option
        : { ...option, disabled: SEMI_AUTO_SEND_TIMED_OPTIONS_DISABLED || isLettings }
  );

export {
  AUTOMATION_MODE_OPTIONS,
  SEMI_AUTOMATIC_AUTO_SEND_OPTIONS,
  SEMI_AUTOMATIC_AUTO_SEND_DAY_VALUES,
  DEFAULT_SEMI_AUTOMATIC_AUTO_SEND_DAYS,
  DEFAULT_UNCONFIGURED_CONTACT_FREQUENCY,
  CONTACT_FREQUENCY_MIN,
  CONTACT_FREQUENCY_MAX,
  AUTOMATION_MODE_ICON,
  AUTOMATION_MODE_ICON_CLASS,
  AUTOMATION_MODE_BADGE_CLASS,
  AUTOMATION_MODE_BADGE_LABEL,
  WITHDRAWN_TEMPLATES_ENABLED,
  getAutomationModeOptions,
  getSemiAutomaticAutoSendOptions,
};
